import { apiBaseUrl } from "./auth";
import { getStoredLanguage } from "../context/I18nContext";
import i18n from "../i18n";

const ADMIN_BASE = `${apiBaseUrl}/api/auth/admin`;
const CONTACT_ADMIN_BASE = `${apiBaseUrl}/api/contact`;

async function request(
  path,
  { accessToken, method = "GET", body, params } = {},
) {
  if (!accessToken) {
    throw new Error(i18n.t("errors.adminRequired"));
  }

  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
    "Accept-Language": getStoredLanguage(),
  };

  const queryParams = params
    ? { ...params, lang: getStoredLanguage() }
    : { lang: getStoredLanguage() };
  const queryString = `?${new URLSearchParams(queryParams).toString()}`;

  const response = await fetch(`${ADMIN_BASE}${path}${queryString}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const isJson = response.headers
    .get("content-type")
    ?.includes("application/json");
  const payload = isJson ? await response.json() : {};

  if (!response.ok) {
    const message = payload.error || i18n.t("errors.operationFailed");
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  return payload;
}

async function contactRequest(
  path,
  { accessToken, method = "GET", body, params } = {},
) {
  if (!accessToken) {
    throw new Error(i18n.t("errors.adminRequired"));
  }

  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
    "Accept-Language": getStoredLanguage(),
  };

  const queryParams = params
    ? { ...params, lang: getStoredLanguage() }
    : { lang: getStoredLanguage() };
  const queryString = `?${new URLSearchParams(queryParams).toString()}`;

  const response = await fetch(`${CONTACT_ADMIN_BASE}${path}${queryString}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const isJson = response.headers
    .get("content-type")
    ?.includes("application/json");
  const payload = isJson ? await response.json() : {};

  if (!response.ok) {
    const message = payload.error || i18n.t("errors.operationFailed");
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  return payload;
}

export function fetchUsers(
  accessToken,
  { page, role, search, is_verified } = {},
) {
  const params = {};
  if (page) params.page = page;
  if (role && role !== "all") params.role = role;
  if (search) params.search = search;
  if (is_verified === true) params.is_verified = "true";
  if (is_verified === false) params.is_verified = "false";
  return request("/users/", {
    accessToken,
    params: Object.keys(params).length ? params : undefined,
  });
}

export function updateUser(
  accessToken,
  { userId, role, is_verified, is_active },
) {
  return request("/users/", {
    accessToken,
    method: "POST",
    body: { user_id: userId, role, is_verified, is_active },
  });
}

export function deleteUser(accessToken, userId) {
  return request("/users/", {
    accessToken,
    method: "POST",
    body: { action: "delete", user_id: userId },
  });
}

export function fetchStudents(accessToken, { page } = {}) {
  const params = page ? { page } : undefined;
  return request("/students/", { accessToken, params });
}

export function fetchPacks(accessToken, { page } = {}) {
  const params = page ? { page } : undefined;
  return request("/packs/", { accessToken, params });
}

export function savePack(accessToken, payload) {
  return request("/packs/", { accessToken, method: "POST", body: payload });
}

export function deletePack(accessToken, packId) {
  return request("/packs/", {
    accessToken,
    method: "POST",
    body: { id: packId, action: "delete" },
  });
}

export function fetchPayments(accessToken, { page } = {}) {
  const params = page ? { page } : undefined;
  return request("/payments/", { accessToken, params });
}

export function updatePayment(accessToken, payload) {
  return request("/payments/", { accessToken, method: "POST", body: payload });
}

export function createPayment(accessToken, payload) {
  return request("/payments/", { accessToken, method: "POST", body: payload });
}

export function fetchSupport(accessToken, { page } = {}) {
  const params = page ? { page } : undefined;
  return contactRequest("/messages/", { accessToken, params });
}

export function markSupportRead(accessToken, messageId) {
  return contactRequest("/messages/", {
    accessToken,
    method: "POST",
    body: { action: "mark_read", message_id: messageId },
  });
}

export function replySupport(accessToken, payload) {
  return contactRequest("/messages/", {
    accessToken,
    method: "POST",
    body: { action: "reply", ...payload },
  });
}

export function fetchStatistics(accessToken) {
  return request("/statistics/", { accessToken });
}
